<template>
  <v-container fluid v-if="loadedSettings">
    <validation-observer ref="form">
      <v-form>
        <v-card outlined class="mb-5">
          <v-card-title style="word-break: normal !important;">
            {{$t("settings.locationDailyMaxAllocations")}}
          </v-card-title>
          <v-container fluid>
            <v-row>
              <v-col cols="12" md="8">
                <v-switch
                  v-model="loadedSettings.maxDailyAllocationsByDefaultActivate"
                  :label="$t('settings.defineLocationDailyMaxAllocationsByDefault')">
                </v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <validation-provider
                  v-if="loadedSettings.maxDailyAllocationsByDefaultActivate"
                  class="width-100"
                  rules="min_value:1|integer|required"
                  name ="location daily max orchestration"
                  v-slot="{ errors }">
                  <v-text-field
                    class="pt-2"
                    v-model="loadedSettings.maxDailyAllocationsByDefaultValue"
                    :label="$t('settings.locationDailyMaxAllocationsByDefault')"
                    :error-messages="errors"
                    required
                    dense
                    type="number"
                    outlined
                  >
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <v-data-table
              dense
              :headers="headers"
              :items="loadedSettings.maxDailyAllocationsPerLocations"
              :loading-text="$t('settings.maxAllocationsLoadingText')"
              :no-data-text="$t('settings.noMaxAllocations')"
              mobile-breakpoint="768"
              item-key="id"
              :footer-props='footerProps'
              :options.sync="options"
              :loading="loading"
            >
              <template v-slot:top>
                <v-toolbar flat >
                  <p>{{ $t('settings.maxDailyAllocationsByLocations') }}</p>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" outlined @click="openAddNewMaxAllocationsModal()">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-toolbar>
              </template>
              <template v-slot:item.locations="{ item }">
                <p :style="orderIdColumn">{{item.locations.join(", ")}}</p>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  class="pl-0 pr-0"
                  color="primary"
                  @click="editSelectedAllocation(item)">
                <v-icon>mdi-square-edit-outline</v-icon>
                </v-btn>
                <v-btn
                  icon
                  class="pl-0 pr-0"
                  color="error"
                  @click='openDeleteAllocationDialog(item)'
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:item.activate="{ item }">
                <v-switch
                  v-model="item.activate">
                </v-switch>
              </template>
            </v-data-table>
            <confirmation-dialog
              v-model="openDeleteItemModal"
              :text="$t('settings.deleteAllocation')"
              :title="$t('settings.deleteAllocationTitle')"
              @no="openDeleteItemModal = false"
              @yes="deleteItem(selectedAllocation)"
            ></confirmation-dialog>

            <location-max-allocations-dialog
              v-if="selectedAllocation.locations"
              v-model="editLocationMaxAllocationModal"
              :title="$t('settings.modifyMaxDailyAllocationsByLocations')"
              :locations="availableLocations"
              :maxAllocations="selectedAllocation"
              @close="closeEditSelectedAllocation"
              @confirm="updateSelectAllocation"
            >
            </location-max-allocations-dialog>

            <location-max-allocations-dialog
              v-if="selectedAllocation.locations"
              v-model="addLocationMaxAllocationModal"
              :title="$t('settings.AddMaxDailyAllocationsByLocations')"
              :locations="availableLocations"
              :maxAllocations="selectedAllocation"
              @close="closeAddSelectedMaxAllocations"
              @confirm="addMaxAllocation"
            >
            </location-max-allocations-dialog>

          </v-container>
        </v-card>
      </v-form>
    </validation-observer>
  </v-container>
</template>


<script>
  import { ValidationProvider, extend, ValidationObserver } from "vee-validate"
  import { integer, min_value, required } from 'vee-validate/dist/rules'
  import confirmationDialog from '@/components/ConfirmationDialog'
  import locationMaxAllocationsDialog from '@/components/dialogs/locationMaxAllocationsDialog'

  extend('required', {
    ...required,
    message: 'This field is required'
  })
  extend("min_value", {
    ...min_value,
    message: (fieldName, placeholders) => {
      let minute = placeholders.unit == undefined ? "" : placeholders.unit;
      return `${fieldName} is < ${placeholders.min} ` + minute;
    },
    params: ["min", "unit"]
  })
  extend('integer', {
    ...integer,
    message: 'floating point numbers are not allowed',
  })

  export default {
    name: "OrchestrationSettings",
    data() {
      return {
        locations: [],
        headers: [
          {
            text:  this.$t('settings.locations'),
            value: "locations"
          },
          {
            text:  this.$t('settings.locationDailyMaxAllocations'),
            value: "value"
          },
          {
            text:  this.$t('settings.active'),
            value: "activate"
          },
          {
            text: "",
            value: "actions",
            sortable: false,
          }
        ],
        options: {
          itemsPerPage: 15
        },
        openDeleteItemModal: false,
        selectedAllocation: {},
        editLocationMaxAllocationModal: false,
        addLocationMaxAllocationModal: false
      }
    },
    components: {
      "validation-provider": ValidationProvider,
      "validation-observer": ValidationObserver,
      "confirmationDialog": confirmationDialog,
      "locationMaxAllocationsDialog": locationMaxAllocationsDialog
    },
    mounted() {
      this.getLocations()
    },
    props: {
      loadedSettings: {
        type: Object,
        required: false,
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    methods: {
      // Form validation
      validateForm() {
        return this.$refs.form.validate()
      },
      getLocations() {  // get all locations
        let myInit = {
          headers: {
            "Authorization": 'Bearer ' + this.$auth.accessToken
          },
          queryStringParameters:{
            itemsPerPage: 100000
          }
        }
        this.$Amplify.API.get("locations", "/v1/int/locations", myInit)
          .then((res) => { this.locations = res.data.map(location => location.location_id) })
          .catch((e) => { console.log(e) })
      },
      deleteItem(){
        this.loadedSettings.maxDailyAllocationsPerLocations.splice(this.loadedSettings.maxDailyAllocationsPerLocations.indexOf(this.selectedAllocation), 1)
        this.openDeleteItemModal = false
        this.selectedAllocation = {}
      },
      openDeleteAllocationDialog(item){
        this.selectedAllocation = item
        this.openDeleteItemModal = true
      },
      editSelectedAllocation(item){
        this.selectedAllocation = item
        this.editLocationMaxAllocationModal = true
      },
      closeEditSelectedAllocation(){
        this.editLocationMaxAllocationModal = false
        this.selectedAllocation = {}
      },
      updateSelectAllocation(updatedAllocation){
        this.selectedAllocation.locations = updatedAllocation.locations
        this.selectedAllocation.value = updatedAllocation.value
        this.closeEditSelectedAllocation()
      },
      openAddNewMaxAllocationsModal(){
        this.selectedAllocation = {activate: true, value: 100, locations: []}
        this.addLocationMaxAllocationModal = true
      },
      closeAddSelectedMaxAllocations(){
        this.addLocationMaxAllocationModal = false
        this.selectedAllocation = {}
      },
      addMaxAllocation(newMaxAllocations){
        this.loadedSettings.maxDailyAllocationsPerLocations.unshift(newMaxAllocations)
        this.closeAddSelectedMaxAllocations()
      }
    },
    computed: {
      footerProps () {
        return {
          "items-per-page-options": [1, 5, 15, 25, 50],
          "items-per-page-text": this.$t("settings.allocationsPerPage")
        }
      },
      orderIdColumn() {
        if(this.$vuetify.breakpoint.width < 450){
          return 'max-width: 150px'
        }
        if(this.$vuetify.breakpoint.width < 650){
          return 'max-width: 250px'
        }
        if(this.$vuetify.breakpoint.width < 768){
          return 'max-width: 400px'
        }
        if(this.$vuetify.breakpoint.width == 768){
          return 'max-width: 400px'
        }
        if(this.$vuetify.breakpoint.width < 1288){
          return 'max-width: 500px'
        }
        else {
          return 'max-width: 600px'
        }
      },
      availableLocations() {
        let alreadySelectedLocations = []
        for(let allocation of this.loadedSettings.maxDailyAllocationsPerLocations){
          if(this.selectedAllocation !== allocation){
            alreadySelectedLocations = [...alreadySelectedLocations, ...allocation.locations]
          }
        }
        return this.locations.filter(location => !alreadySelectedLocations.includes(location))
      },
      isMobile(){
        return this.$vuetify.breakpoint.width < 650
      },
    }
  }
</script>
